import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import _ from 'lodash';

import type { UploadErrorListener } from '@/lib/file-uploader';
import { isPhoneValid } from '@/lib/phone-utils';
import { mixpanel } from '@/api/app/mixpanel';
import {
  ticketChannelSelector,
  ticketContactPhoneSelector,
  ticketHasNotesSelector,
  ticketContactAvochatoAccountSelector,
} from '@/redux/app/selectors/ticket';
import { UserInfoContext } from '../../../user/user-info';

import { TicketOwnerSelector } from '../../TicketOwnerSelector';
import { TicketStatusSelector } from '../../TicketStatusSelector';

import { TicketContact } from './TicketContact';
import { TicketTagList } from './TicketTagList';
import { TicketDeviceBin } from './TicketDeviceBin';
import { LookupButton } from './LookupButton';
import { TicketBackButton } from './TicketBackButton';
import { UploadProgressBar } from './UploadProgressBar';
import { TicketFileUploaderContext } from '../ticket-file-uploader.context';

export interface TicketHeaderProps {
  ticket: string;
  handleNotesClick?: () => void;
}

const useClickOutside = (ref: any, callback: any) => {
  React.useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (ref.current && !ref.current.contains(event.target)) callback(false);
    };
    window.document.addEventListener('click', handleClickOutside);

    return () => {
      window.document.removeEventListener('click', handleClickOutside);
    };
  }, [ref, callback]);
};

export const TicketHeader = ({ ticket, handleNotesClick }: TicketHeaderProps) => {
  const { canCall, admin, avochatoRep } = useContext(UserInfoContext);
  const channel = useSelector(_.partial(ticketChannelSelector, ticket));
  const phone = useSelector(_.partial(ticketContactPhoneSelector, ticket));
  const hasNotes = useSelector(_.partial(ticketHasNotesSelector, ticket));
  const avochatoAccount = useSelector(_.partial(ticketContactAvochatoAccountSelector, ticket));

  const [tagFormVisible, setTagFormVisible] = useState(false);
  const [deviceBinVisible, setDeviceBinVisible] = useState(false);
  const TicketTagListRef = React.createRef();
  useClickOutside(TicketTagListRef, setTagFormVisible);

  const handleDeviceBinPress = useCallback(() => {
    setDeviceBinVisible(!deviceBinVisible);
  }, [deviceBinVisible]);

  const handleTagsPress = useCallback(() => {
    setTagFormVisible(!tagFormVisible);
  }, [tagFormVisible]);

  const [uploadVisible, setUploadVisible] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(null);
  const [uploadError, setUploadError] = useState<string>(null);
  const fileUploader = useContext(TicketFileUploaderContext);
  useEffect(() => {
    if (!fileUploader) return undefined;

    const startListener = () => {
      setUploadError(null);
      setUploadProgress(0.25);
      setUploadVisible(true);
    };
    const completeListener = () => {
      setUploadProgress(2);
      setTimeout(() => {
        setUploadVisible(false);
        setTimeout(() => setUploadProgress(null), 500);
      }, 1000);
    };
    const errorListener: UploadErrorListener = (event) => {
      const { error } = event.detail;
      setUploadProgress(-1);
      setUploadError(error.userMessage);
      mixpanel.track('S3 Upload Failed', { 'Error Message': error.message, ...error.context });
    };
    const progressListener = _.throttle((event) => {
      const { uploadedRatio } = event.detail;
      setUploadProgress(uploadedRatio * 0.85 + 0.3);
    }, 300);
    fileUploader.addUploadStartListener(startListener);
    fileUploader.addUploadProgressListener(progressListener);
    fileUploader.addUploadCompleteListener(completeListener);
    fileUploader.addUploadErrorListener(errorListener);
    return () => {
      fileUploader.removeUploadStartListener(startListener);
      fileUploader.removeUploadProgressListener(progressListener);
      fileUploader.removeUploadCompleteListener(completeListener);
      fileUploader.removeUploadErrorListener(errorListener);
    };
  }, [fileUploader]);

  return (
    <div className="ticket-header">
      <TicketBackButton />
      <div className="ticket-info">
        <TicketContact ticket={ticket} handleDeviceBinPress={handleDeviceBinPress} />
        <div className="button-bin">
          <TicketStatusSelector ticket={ticket} selectorClassName="ticket-show" />
          <TicketOwnerSelector ticket={ticket} />
          {canCall && isPhoneValid(phone) && channel === 'sms' && (
            <button
              type="button"
              className="call-button noselect"
              data-phone={phone}
              onClick={window.dial}
            >
              
            </button>
          )}
          <button
            type="button"
            className="tag-form-toggler revealer noselect"
            onClick={handleTagsPress}
            onKeyPress={handleTagsPress}
          >
            Tags
          </button>
          <button
            type="button"
            className={classNames('notes-button', hasNotes && 'has-notes', 'noselect')}
            onClick={handleNotesClick}
          >
            
          </button>
          <LookupButton
            admin={admin}
            subdomain={avochatoAccount}
            avochatoRep={avochatoRep}
          />
        </div>
      </div>
      <UploadProgressBar
        visible={uploadVisible}
        progress={uploadProgress}
        text={uploadError ?? (
          uploadProgress > 0 && ( // eslint-disable-next-line no-nested-ternary
            uploadProgress < 0.3 ? 'Scanning...'
              : uploadProgress === 2 ? 'Uploading done!'
                : `Uploading... (${Math.min(Math.floor(uploadProgress * 100), 100)}%)`
          )
        )}
      />
      <TicketTagList ticket={ticket} show={tagFormVisible} ref={TicketTagListRef} />
      <TicketDeviceBin ticket={ticket} show={deviceBinVisible} />
    </div>
  );
};
